import { useCallback, useEffect, useState } from "react";
import { ListChecks } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { InlineLoader } from "@/components/common/Loader";
import { OPS } from "@/constants/testIds";
import { RecordList } from "@/components/waste-meals/RecordPanel";

const C = OPS.wm;

/**
 * «إدخالاتي» — the caller's own records across the WHOLE edit window, not
 * just today: an employee who made a mistake yesterday needs to reach it.
 * `GET /records` forces `created_by = self` below `wm_branch_manage`, so this
 * list is isolated on the server, not by this component.
 */
export function MyEntries({ scope, catalogue, branchId, type, reloadKey }) {
    const { t } = useI18n();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        if (!branchId) return;
        setLoading(true);
        try {
            const res = await api.get("/waste-meals/records", {
                params: {
                    branch_id: branchId, type,
                    from: scope?.min_date || scope?.today, to: scope?.today,
                    page_size: 100,
                },
            });
            setRows(res.data.rows || []);
        } finally {
            setLoading(false);
        }
    }, [branchId, type, scope]);

    useEffect(() => { load(); }, [load, reloadKey]);

    return (
        <section data-testid={C.myEntries.list}
                 className="rounded-[var(--radius-lg)] border border-[color:var(--border-soft)] bg-[color:var(--bg-surface)] p-3">
            <header className="flex items-center gap-2">
                <ListChecks className="h-4 w-4 text-[color:var(--brand-brown)]" />
                <h3 className="text-sm font-bold text-[color:var(--ink)]">{t("wm_my_entries")}</h3>
                <span className="text-[11px] text-[color:var(--ink-faint)]">{t("wm_my_entries_hint")}</span>
            </header>
            {loading ? <InlineLoader /> : rows.length ? (
                <RecordList rows={rows} catalogue={catalogue} testIdRow={C.myEntries.row}
                            testIdEdit={C.myEntries.edit} testIdDelete={C.myEntries.del}
                            testIdReturn={C.myEntries.returnBadge}
                            onChanged={load} />
            ) : (
                <p data-testid={C.myEntries.empty} className="py-5 text-center text-sm text-[color:var(--ink-muted)]">
                    {t("wm_my_entries_empty")}
                </p>
            )}
        </section>
    );
}
